// The Developer section's Activity (DSEC D2; apiv2/developer.openapi.yaml `developerActivity`): the
// tenant's audit log of client-facing events. Words for the contract's `ActivityType` enum (checked by
// developerContract.test.js), the type groups of the filter, and the item's shapes.
import type { Label } from './labels';
import { pagePath } from './routes';

export interface ActivityTenant {
    id: number;
    code: string;
    name: string;
    environment: 'production' | 'sandbox';
    dashboard_url: string;
}

/** `user`: of this tenant or its pair (with e-mail); `platform`: Curated Planet; `other`: "Another account"
 *  (another tenant's user) or "A deleted user" -- never with an e-mail or an IP. */
export interface ActivityActor {
    kind: 'user' | 'platform' | 'other';
    name: string;
    email: string | null;
}

export interface ActivityTarget {
    kind: 'api_key' | 'webhook_subscription' | 'notification' | 'tenant';
    id: number;
    name: string;
}

export interface ActivityLink {
    page: 'api_keys' | 'webhook_deliveries' | 'overview' | 'mcp_connections' | 'integrations';
    id: number | null;
}

export interface ActivityItem {
    id: number;
    type: string;
    at: string;
    severity: 'info' | 'warning' | 'critical';
    tenant: ActivityTenant | null;
    actor: ActivityActor | null;
    ip: string | null;
    target: ActivityTarget | null;
    link: ActivityLink | null;
    title: string;
    text: string;
    data: Record<string, any>;
    platform: boolean;
}

export const ACTIVITY_TYPES: Record<string, Label> = {
    api_credential_created: { label: 'API key created', tone: 'info' },
    api_credential_updated: { label: 'API key edited', tone: 'info' },
    api_credential_revoked: { label: 'API key revoked', tone: 'warning' },
    api_credential_rotated: { label: 'API key rotated', tone: 'info' },
    api_credential_expiring: { label: 'API key expiring', tone: 'warning' },
    api_credential_expired: { label: 'API key expired', tone: 'error' },
    sandbox_created: { label: 'Sandbox created', tone: 'info' },
    sandbox_reset: { label: 'Sandbox reset', tone: 'info' },
    sandbox_suspended: { label: 'Sandbox suspended', tone: 'warning' },
    sandbox_resumed: { label: 'Sandbox resumed', tone: 'success' },
    sandbox_outbound_changed: { label: 'Outbound settings changed', tone: 'info' },
    sandbox_production_key: { label: 'Production key', tone: 'warning', hint: "The sandbox's vendor credentials and production's." },
    sandbox_signin: { label: 'Sandbox sign-in', tone: 'info' },
    sandbox_sms_set_up: { label: 'Sandbox SMS set up', tone: 'info' },
    sandbox_sms_removed: { label: 'Sandbox SMS removed', tone: 'info' },
    webhook_subscription_created: { label: 'Webhook added', tone: 'info' },
    webhook_subscription_deleted: { label: 'Webhook removed', tone: 'info' },
    mcp_connection_granted: { label: 'MCP connection granted', tone: 'info' },
    mcp_connection_revoked: { label: 'MCP connection revoked', tone: 'info' },
    sandbox_breaker_tripped: { label: 'Circuit breaker tripped', tone: 'error' },
    sandbox_breaker_reset: { label: 'Circuit breaker reset', tone: 'success' },
    sandbox_quota_exceeded: { label: 'Quota reached', tone: 'warning' },
    sandbox_quota_changed: { label: 'Quotas changed', tone: 'info' },
    sandbox_ingress_changed: { label: 'Ingress rule changed', tone: 'info' },
    sandbox_mirror_stopped: { label: 'Mirror stopped', tone: 'warning' },
    // ESB: the mirror switched from production's sandbox card (a row in each tenant).
    sandbox_mirror_changed: { label: 'Mirror switched', tone: 'warning' },
    sandbox_admin_added: { label: 'Sandbox admin added', tone: 'info' },
    // DSEC INT: Developer › Integrations (the AuditEvent types are I3's; words here keep the contract test green).
    integration_added: { label: 'Booking system added', tone: 'info' },
    integration_removed: { label: 'Booking system removed', tone: 'warning' },
    integration_account_added: { label: 'Booking-system account added', tone: 'info' },
    integration_account_removed: { label: 'Booking-system account removed', tone: 'warning' },
    integration_settings_changed: { label: 'Integration settings changed', tone: 'info' },
    integration_credentials_replaced: { label: 'Integration credentials replaced', tone: 'info' },
    integration_webhook_token_viewed: { label: 'Webhook token viewed', tone: 'info' },
    integration_webhooks_installed: { label: 'Webhooks installed', tone: 'info' },
    // Backend plan 2026-10-29 sdtc WP-D: one row per change of the operator's default tag categories, from wherever it
    // was made (Trip defaults, the Integrations grid, a sandbox's sync with live, onboarding, the console). The server's
    // title and text say where and what (category codes and setting names only).
    tour_operator_default_tag_categories_changed: { label: 'Default tag categories changed', tone: 'info' },
    // Client MCP (backend plan 2026-10-20): the auto-apply switch or its ops lock (P0-10), a spend ceiling or cap
    // of the MCP tools reached (P0-07, §7.6), the guest-data opt-in (P1b-06, Settings › AI editing), a production
    // write of the developer tools (P1b-06), a support ticket the client's AI filed and support moving it on (P1-10),
    // a booking refresh requested over MCP (P1-12).
    mcp_auto_apply_changed: { label: 'MCP auto-apply changed', tone: 'info' },
    mcp_limit_reached: { label: 'MCP limit reached', tone: 'warning' },
    mcp_pii_opt_in_changed: { label: 'Guest data over MCP changed', tone: 'warning' },
    mcp_developer_action: { label: 'Developer action over MCP', tone: 'info' },
    support_ticket_created: { label: 'Support ticket filed', tone: 'info' },
    support_ticket_status_changed: { label: 'Support ticket updated', tone: 'info' },
    booking_resync_requested: { label: 'Booking refresh requested', tone: 'info' },
    // Client MCP P2-07: a guest list exported over MCP, and its file downloaded (the facts only, never the data); facts
    // of record, not alarms (review batch 3 L9).
    analytics_export_created: { label: 'Guest list exported over MCP', tone: 'info' },
    analytics_export_downloaded: { label: 'Guest list downloaded', tone: 'info' },
};

export const activityType = (value: string): Label => ACTIVITY_TYPES[value] || { label: value, tone: 'neutral' };

/** The filter's groups: each one asks for its types (`type=a,b,…`). */
export const ACTIVITY_GROUPS: Array<{ key: string; label: string; types: string[] }> = [
    { key: 'keys', label: 'API keys', types: Object.keys(ACTIVITY_TYPES).filter((t) => t.startsWith('api_credential_')) },
    { key: 'sandbox', label: 'Sandbox', types: Object.keys(ACTIVITY_TYPES).filter((t) => t.startsWith('sandbox_')) },
    { key: 'webhooks', label: 'Webhooks', types: Object.keys(ACTIVITY_TYPES).filter((t) => t.startsWith('webhook_subscription_')) },
    { key: 'mcp', label: 'MCP connections', types: Object.keys(ACTIVITY_TYPES).filter((t) => t.startsWith('mcp_connection_')) },
    {
        key: 'mcp_ai',
        label: 'AI over MCP',
        types: [
            'mcp_auto_apply_changed',
            'mcp_limit_reached',
            'mcp_pii_opt_in_changed',
            'mcp_developer_action',
            'support_ticket_created',
            'support_ticket_status_changed',
            'booking_resync_requested',
            'analytics_export_created',
            'analytics_export_downloaded',
        ],
    },
    {
        key: 'integrations',
        label: 'Integrations',
        types: [...Object.keys(ACTIVITY_TYPES).filter((t) => t.startsWith('integration_')), 'tour_operator_default_tag_categories_changed'],
    },
];

// The account menu's page (App.js `/mcp-connections`, layout/UserMenu.js MCP_CONNECTIONS_PATH).
const MCP_CONNECTIONS_PATH = '/mcp-connections';

/** Where an item's link goes: `{ to }` inside this dashboard, `{ href }` on the other environment's. */
export const activityHref = (item: ActivityItem, environment: string): { to?: string; href?: string } | null => {
    if (!item.link) {
        return null;
    }
    const path = item.link.page === 'mcp_connections' ? MCP_CONNECTIONS_PATH : pagePath(item.link.page);
    if (item.tenant && item.tenant.environment !== environment) {
        return { href: `${item.tenant.dashboard_url.replace(/\/$/, '')}${path}` };
    }
    return { to: path };
};
